import React from "react";
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import dayjs from "dayjs";
import {
  pathBomStockStatusLabel,
  PATH_BOM_STOCK_STATUS,
} from "../utils/pathBomStockCheck";
import {
  FORM_CANCEL_BUTTON_SX,
  FORM_SUBMIT_BUTTON_SX,
  STANDARD_FORM_DIALOG_ACTIONS_SX,
  STANDARD_FORM_DIALOG_PAPER_SX,
  STANDARD_FORM_DIALOG_TITLE_SX,
} from "../utils/formModalStyles";

const statusChipColor = (status) => {
  if (status === PATH_BOM_STOCK_STATUS.OK) return "success";
  if (status === PATH_BOM_STOCK_STATUS.INSUFFICIENT_STOCK) return "warning";
  return "error";
};

/**
 * Popup when pathology BOM / consumable stock is insufficient
 * (sample collection or add/update result). Shows available qty, batches, expiry.
 */
const PathBomInsufficientStockDialog = ({
  open,
  onClose,
  checks = [],
  actionLabel = "continue",
  title = "Insufficient pathology consumable stock",
}) => {
  const blocking = (Array.isArray(checks) ? checks : []).filter((c) => c?.blocking);
  const rows = blocking.length > 0 ? blocking : checks;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      sx={STANDARD_FORM_DIALOG_PAPER_SX}
    >
      <DialogTitle sx={STANDARD_FORM_DIALOG_TITLE_SX}>{title}</DialogTitle>
      <DialogContent>
        <Alert severity="error" sx={{ mb: 2 }}>
          <AlertTitle>Cannot {actionLabel}</AlertTitle>
          One or more lab-test consumables (BOM / path items) do not have enough
          usable inventory. Add or restock pathology inventory, then try again.
        </Alert>

        {rows.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            No stock details available.
          </Typography>
        ) : (
          <TableContainer sx={{ maxHeight: 420 }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell>#</TableCell>
                  <TableCell>Consumable</TableCell>
                  <TableCell>Lab test(s)</TableCell>
                  <TableCell align="right">Need</TableCell>
                  <TableCell align="right">Available</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Batch / Expiry</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((check, index) => {
                  const batches = Array.isArray(check.usableBatches)
                    ? check.usableBatches
                    : [];
                  return (
                    <TableRow key={`${check.bomId ?? check.itemName}-${index}`} hover>
                      <TableCell>{index + 1}</TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight={600}>
                          {check.itemName || "—"}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="caption" color="text.secondary">
                          {(check.labTestNames || []).join(", ") || "—"}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">{check.requiredQty ?? 0}</TableCell>
                      <TableCell align="right">
                        <Typography
                          variant="body2"
                          fontWeight={700}
                          color={
                            Number(check.availableQty) < Number(check.requiredQty)
                              ? "error.main"
                              : "text.primary"
                          }
                        >
                          {check.availableQty ?? 0}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          label={pathBomStockStatusLabel(check.status)}
                          color={statusChipColor(check.status)}
                          variant="outlined"
                        />
                        {check.message && (
                          <Typography
                            variant="caption"
                            display="block"
                            color="text.secondary"
                            sx={{ mt: 0.5, maxWidth: 220 }}
                          >
                            {check.message}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        {batches.length === 0 ? (
                          <Typography variant="caption" color="text.secondary">
                            No usable batches
                          </Typography>
                        ) : (
                          <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75 }}>
                            {batches.slice(0, 4).map((batch, bIndex) => (
                              <Box key={`${batch.inventoryId}-${bIndex}`}>
                                <Typography variant="caption" display="block">
                                  <strong>Batch:</strong> {batch.batchNumber || "N/A"}
                                  {" · "}
                                  <strong>Qty:</strong> {batch.quantity}
                                  {batch.unitType ? ` ${batch.unitType}` : ""}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  Expiry:{" "}
                                  {batch.expiryDate
                                    ? dayjs(batch.expiryDate).format("DD MMM YYYY")
                                    : "N/A"}
                                </Typography>
                              </Box>
                            ))}
                            {batches.length > 4 && (
                              <Typography variant="caption" color="text.secondary">
                                +{batches.length - 4} more batch(es)
                              </Typography>
                            )}
                          </Box>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </DialogContent>
      <DialogActions sx={STANDARD_FORM_DIALOG_ACTIONS_SX}>
        <Button onClick={onClose} sx={FORM_CANCEL_BUTTON_SX}>
          Close
        </Button>
        <Button onClick={onClose} variant="contained" sx={FORM_SUBMIT_BUTTON_SX}>
          OK
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PathBomInsufficientStockDialog;
